import { graphql } from 'react-relay';
import type { FC } from 'react';
import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

// eslint-disable-next-line check-choreographer-usage/no-import-in-product-messages-without-choreographer
import Popup from '@atlaskit/popup';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { layers } from '@atlaskit/theme/constants';

import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import {
	useApprovals,
	useApprovalsDispatch,
} from '@confluence/approvals/entry-points/ApprovalsContext.ts';
import type { WorkflowStatus } from '@confluence/approvals/entry-points/ApprovalsContext.ts';
import {
	APPROVALS_ANALYTICS_SOURCE,
	getFailureStatusCode,
} from '@confluence/approvals/entry-points/analytics.ts';
import { ApprovalActionConfirmationModal } from '@confluence/approvals/entry-points/ApprovalActionConfirmationModal.ts';
import { ChangeStatusAndCancelApprovalModal } from '@confluence/approvals/entry-points/ChangeStatusAndCancelApprovalModal.ts';
import { CancelApprovalModal } from '@confluence/approvals/entry-points/CancelApprovalModal.ts';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { confluenceLocalStorageInstance as localStorage } from '@confluence/storage-manager/entry-points/storageInstance.ts';
import { keys } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';
import { fg } from '@confluence/feature-gating';
import { expValEquals } from '@confluence/feature-experiments';
import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
import {
	APPROVALS_PANE_ID,
	openApprovalsPaneForContext,
} from '@confluence/approvals/entry-points/approvalsPaneSidebar.ts';
import { useRightSidebarContext } from '@confluence/page-layout-context';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useRenderServerPlaceholder } from '@confluence/ssr-utilities/entry-points/useRenderServerPlaceholder.ts';

import { useUserProfileData } from '../hooks/useUserProfileData.ts';
import { colorCodeToColorName, tokenToHex } from '../helpers.ts';
import { LiveDocStartApprovalPopup } from '../ContentStateInfoPopup/ApprovalStatusButton/LiveDocStartApprovalPopup.tsx';
import { ContentStateInfoPopup } from '../ContentStateInfoPopup/ContentStateInfoPopup.tsx';
import { OtherModeReviewBlockingPopup } from '../OtherModeReviewBlockingPopup/OtherModeReviewBlockingPopup.tsx';

import { ContentStateSkeleton } from './ContentStateSkeleton.tsx';
import { ContentStateButton } from './ContentStateButton.tsx';
import { ContentStateMenu } from './ContentStateMenu.tsx';
import { ContentStateMenuV2 } from './ContentStateMenuV2/ContentStateMenuV2.tsx';
import {
	createNewCustomStatesArrayWhenExistingCustomSelected,
	createNewCustomStatesArrayWhenNewCustomAdded,
	reconcileCustomStatesAfterCreate,
	isDuplicate,
} from './helpers.ts';
import type { ContentState, EditorContentStateProps } from './types.ts';
import { i18n } from './i18n.ts';
import EditorContentStateQuery from './__generated__/EditorContentStateQuery.graphql.ts';
import type {
	EditorContentStateComponentSetContentStateMutation as EditorContentStateComponentSetContentStateMutation$data,
	EditorContentStateComponentSetContentStateMutationVariables as EditorContentStateComponentSetContentStateMutation$variables,
} from './__types__/EditorContentStateComponentSetContentStateMutation.ts';
import type {
	EditorContentStateComponentRemoveContentStateMutation as EditorContentStateComponentRemoveContentStateMutation$data,
	EditorContentStateComponentRemoveContentStateMutationVariables as EditorContentStateComponentRemoveContentStateMutation$variables,
} from './__types__/EditorContentStateComponentRemoveContentStateMutation.ts';
import type {
	EditorContentStateComponentSetContentStateAndPublishMutation$data,
	EditorContentStateComponentSetContentStateAndPublishMutation$variables,
} from './__generated__/EditorContentStateComponentSetContentStateAndPublishMutation.graphql.ts';
import type {
	EditorContentStateComponentRemoveContentStateAndPublishMutation$data,
	EditorContentStateComponentRemoveContentStateAndPublishMutation$variables,
} from './__generated__/EditorContentStateComponentRemoveContentStateAndPublishMutation.graphql.ts';
import type {
	EditorContentStateComponentDisableCustomContentStateMutation$data,
	EditorContentStateComponentDisableCustomContentStateMutation$variables,
} from './__generated__/EditorContentStateComponentDisableCustomContentStateMutation.graphql.ts';

/**
 * A status change the user selected while an end-state approval (APPROVED / DENIED)
 * is active. Stashed so it can be applied only after the user confirms the
 * "Change status?" friction modal. PENDING approvals are handled separately via
 * the cancel-approval flow and never produce a value here.
 *
 * The `workflow` kind is for selecting the review-eligible ("In Review") status again,
 * which on confirm cancels the old approval and starts a fresh approval request.
 */
type PendingStatusChange =
	| { kind: 'existing'; status: ContentState }
	| { kind: 'custom'; name: string; colorId: string }
	| { kind: 'remove' }
	| { kind: 'workflow'; status: WorkflowStatus };

export const EditorContentStateComponentImpl: FC<EditorContentStateProps> = ({
	contentId,
	contentType,
	contentStateState,
	userOffline,
	flags,
	forceContentStatePlacementToTop,
	onUpdateSuccess,
	onContentStateMenuToggle,
	floatingToolbarButtonStyle,
	renderPlusButtonEmptyState,
	isDetailsPanel,
	onClick,
	isViewMode,
	canEdit = true,
}) => {
	const contentStateButtonRef = useRef(null);
	const [isContentStateMenuOpen, setIsContentStateMenuOpen] = useState<boolean>(false);
	const [isShowingInfoPopup, setIsShowingInfoPopup] = useState<boolean>(false);
	const [isShowingOtherModeBlockingPopup, setIsShowingOtherModeBlockingPopup] =
		useState<boolean>(false);
	const [isShowingLiveDocStartApprovalPopup, setIsShowingLiveDocStartApprovalPopup] =
		useState<boolean>(false);
	const [isCreatingContentState, setIsCreatingContentState] = useState<boolean>(false);
	const [selectedColor, setSelectedColor] = useState<string | null>(null);
	const [isColorPickerOpen, setIsColorPickerOpen] = useState<boolean>(false);
	const [isHoveringRemove, setIsHoveringRemove] = useState<boolean>(false);
	const [showChangeStatusModal, setShowChangeStatusModal] = useState(false);

	const originalContentState = useRef(contentStateState.state.contentState);

	// Track if we're creating a NEW custom status (V1) vs selecting existing or V2 optimistic
	// Used to determine if onCompleted should update customStatesArray
	const isCreatingNewCustomStatusRef = useRef<boolean>(false);

	// Snapshot of the custom statuses list taken right before an optimistic delete,
	// so the disable mutation's failure handler can restore it.
	const customStatesBeforeDeleteRef = useRef<(ContentState | null)[]>([]);

	// Guards against concurrent deletes. The snapshot ref above only holds a single
	// in-flight operation, so we ignore additional delete clicks until the current
	// disable mutation resolves (success or failure).
	const isDeletingCustomStatusRef = useRef<boolean>(false);

	// Restore the optimistically-removed option after a failed custom-status delete
	// (network error or a 200 with success: false / errors), and surface an error.
	const restoreAfterFailedDelete = useCallback(() => {
		isDeletingCustomStatusRef.current = false;
		// Restore the rollback list before releasing the in-flight guard, so a refetch
		// can't overwrite it with stale server data.
		void contentStateState.setCustomStatesArray(customStatesBeforeDeleteRef.current);
		void contentStateState.setIsUpdating(false);
		void flags.showErrorFlag({
			id: 'content-state-delete-error-flag',
			title: <FormattedMessage {...i18n.errorFlagTitle} />,
			description: <FormattedMessage {...i18n.errorFlagBody} />,
		});
	}, [contentStateState, flags]);

	const setIsContentStateMenuOpenWithListener = useCallback(
		(newValue: boolean) => {
			onContentStateMenuToggle?.(newValue);
			setIsContentStateMenuOpen(newValue);
		},
		[onContentStateMenuToggle],
	);

	const isLivePage = useIsCurrentPageLive();
	const { cloudId } = useSessionData();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	// Suppress the loading skeleton during SSR/initial-hydration to avoid a CLIENT_MISMATCH
	// hydration error: the server renders no skeleton (multiple sources of truth combine to
	// `false`), but the client's first render of the real ApprovalsProvider has
	// `approvalRequestLoading=true` (initial useState before useEffect runs the fetch).
	// Rendering the skeleton only after hydration completes keeps the first client render
	// identical to the server render. See PR #349185 / CCIMPORTER-1635 follow-up.
	const renderServerPlaceholder = useRenderServerPlaceholder();
	const contentState = contentStateState.state.contentState;
	const contentStateLastUpdated = contentStateState.state.contentStateLastUpdated;

	// Approvals context hooks (safe to call unconditionally — returns defaults when provider absent)
	const {
		availableWorkflowStatuses,
		approvalRequest,
		approvalRequestLoading,
		otherModeReviewStatus,
		isContentApprovalsEnabled,
	} = useApprovals();
	const {
		createWorkflowApplication,
		cancelWorkflowApplication,
		clearApprovalRequestOptimistically,
		requestLiveDocApprovalConversion,
	} = useApprovalsDispatch();

	const activeWorkflowStatus = useMemo((): WorkflowStatus | null => {
		const activeStatusId = approvalRequest?.application?.workflowStatusId;
		if (!activeStatusId) return null;
		return availableWorkflowStatuses.find((s) => s.id === activeStatusId) ?? null;
	}, [approvalRequest, availableWorkflowStatuses]);

	// Memoize the review-eligible statuses — empty when space-level approvals toggle is off
	const reviewEligibleStatuses = useMemo(
		() =>
			isContentApprovalsEnabled ? availableWorkflowStatuses.filter((s) => s.allowsReviews) : [],
		[availableWorkflowStatuses, isContentApprovalsEnabled],
	);

	/**
	 * True when there is an active approval review in an *end state* (APPROVED / DENIED).
	 * These are the only reviews that surface the "Change status" action in the info popup;
	 * PENDING reviews surface "Cancel approval" and are handled by the separate cancel flow.
	 */
	const hasEndStateApproval =
		!!activeWorkflowStatus &&
		!!approvalRequest?.review &&
		approvalRequest.review.reviewStatus !== 'CANCELED' &&
		approvalRequest.review.reviewStatus !== 'PENDING';

	const respondedApproverData = useMemo(() => {
		if (!approvalRequest?.review?.approvers) return [];
		return approvalRequest.review.approvers.reduce<
			Array<{ name: string; src?: string; email?: string }>
		>((acc, a) => {
			if (a.status !== null && a.status !== 'PENDING_REVIEW') {
				acc.push({ name: a.name, src: a.avatarUrl, email: a.email });
			}
			return acc;
		}, []);
	}, [approvalRequest]);

	// Fetch user data for content state (skip when workflow status is active — that data comes from ApprovalsContext)
	const shouldFetchUserData =
		!activeWorkflowStatus &&
		contentState?.lastModifierAaid &&
		expValEquals('confluence_approvals_eap', 'isEnabled', true);
	const { updatedBy: contentStateUpdatedBy, loading: contentStateUserLoading } = useUserProfileData(
		shouldFetchUserData ? contentState.lastModifierAaid : null,
	);

	// Resolve update metadata: workflow status data from ApprovalsContext,
	// content state data from the query above
	const updatedBy = activeWorkflowStatus
		? approvalRequest?.application?.applicationLastModifiedBy
		: contentStateUpdatedBy;
	const updatedAt = activeWorkflowStatus
		? approvalRequest?.application?.applicationLastModifiedDate || undefined
		: (contentStateLastUpdated ?? undefined);
	const userDataLoading = activeWorkflowStatus ? false : contentStateUserLoading;

	const [shouldSetAutomationFlag, setShouldSetAutomationFlag] = useState(() => {
		return (
			!localStorage?.getItemAsBoolean(keys.AUTOMATION_DISCOVER_SET_CONTENT_STATE) &&
			fg('cc_a4c_smart_button_changeboarding')
		);
	});
	const [removeContentStateMutation] = useMutationInterop<
		EditorContentStateComponentRemoveContentStateMutation$data,
		EditorContentStateComponentRemoveContentStateMutation$variables
	>(
		graphql`
			mutation EditorContentStateComponentRemoveContentStateMutation($contentId: ID!) {
				removeContentState(contentId: $contentId)
			}
		`,
		{
			refetchQueries: () => [
				{
					query: EditorContentStateQuery,
					variables: {
						contentId,
						isDraft: !isLivePage,
						shouldSkipContentStateLastUpdated: expValEquals(
							'confluence_approvals_eap',
							'isEnabled',
							true,
						)
							? false
							: !isLivePage,
						isApprovalsFgOn: expValEquals('confluence_approvals_eap', 'isEnabled', true),
						format: expValEquals('confluence_approvals_eap', 'isEnabled', true)
							? 'MILLIS'
							: 'USER_FRIENDLY',
					},
				},
			],
			onCompleted: () => {
				onUpdateSuccess?.();
			},
		},
	);
	const [setContentStateMutation] = useMutationInterop<
		EditorContentStateComponentSetContentStateMutation$data,
		EditorContentStateComponentSetContentStateMutation$variables
	>(
		graphql`
			mutation EditorContentStateComponentSetContentStateMutation(
				$contentId: ID!
				$contentStateInput: ContentStateInput!
			) {
				setContentState(contentId: $contentId, contentState: $contentStateInput) {
					name
					color
					id
					restrictionLevel
				}
			}
		`,
		{
			onError: () => {
				isCreatingNewCustomStatusRef.current = false;
				void contentStateState.setContentState(originalContentState.current);
				void flags.showErrorFlag({
					id: 'content-state-error-flag',
					title: <FormattedMessage {...i18n.errorFlagTitle} />,
					description: <FormattedMessage {...i18n.errorFlagBody} />,
				});
				createAnalyticsEvent({
					type: 'sendScreenEvent',
					data: {
						objectId: contentId,
						name: 'contentStateErrorFlag',
						attributes: {
							contentStatus: isViewMode ? 'CURRENT' : 'DRAFT',
							isLivePage,
						},
					},
				}).fire();
			},
			onCompleted: (data) => {
				// Only update customStatesArray when creating a NEW custom status (V1 flow)
				// V2 handles this optimistically before the mutation
				if (data.setContentState && isCreatingNewCustomStatusRef.current) {
					// The approvals-beta flow adds an optimistic placeholder that must be reconciled
					// away; the legacy (gate-off) flow keeps its original dedupe behaviour untouched.
					const newCustomStatesArray = fg('confluence_approvals_beta')
						? reconcileCustomStatesAfterCreate(
								contentStateState.state.customStatesArray,
								data.setContentState,
							)
						: createNewCustomStatesArrayWhenNewCustomAdded(
								contentStateState.state.customStatesArray,
								data.setContentState,
							);
					void contentStateState.setCustomStatesArray(newCustomStatesArray);
					isCreatingNewCustomStatusRef.current = false;
				}
				if (data.setContentState) {
					originalContentState.current = data.setContentState;
				}
				onUpdateSuccess?.();
			},
			refetchQueries: () => [
				{
					query: EditorContentStateQuery,
					variables: {
						contentId,
						isDraft: !isLivePage,
						shouldSkipContentStateLastUpdated: expValEquals(
							'confluence_approvals_eap',
							'isEnabled',
							true,
						)
							? false
							: !isLivePage,
						isApprovalsFgOn: expValEquals('confluence_approvals_eap', 'isEnabled', true),
						format: expValEquals('confluence_approvals_eap', 'isEnabled', true)
							? 'MILLIS'
							: 'USER_FRIENDLY',
					},
				},
			],
		},
	);

	const [disableCustomContentStateMutation] = useMutationInterop<
		EditorContentStateComponentDisableCustomContentStateMutation$data,
		EditorContentStateComponentDisableCustomContentStateMutation$variables
	>(
		graphql`
			mutation EditorContentStateComponentDisableCustomContentStateMutation(
				$cloudId: ID!
				$contentStateId: ID!
			) {
				confluence_disableCustomContentState(cloudId: $cloudId, contentStateId: $contentStateId) {
					success
					errors {
						message
					}
				}
			}
		`,
		{
			onError: () => {
				restoreAfterFailedDelete();
			},
			onCompleted: (data) => {
				// A 200 response can still represent a server-side failure: inspect the
				// payload's success/errors fields and treat a failure like onError so we
				// don't leave the UI in an optimistically-removed state.
				const payload = data?.confluence_disableCustomContentState;
				const failed = !payload?.success || (payload?.errors?.length ?? 0) > 0;
				if (failed) {
					restoreAfterFailedDelete();
					return;
				}
				// The disable succeeded server-side. The option was already removed from the
				// menu optimistically; the page keeps any applied status untouched.
				isDeletingCustomStatusRef.current = false;
				void contentStateState.setIsUpdating(false);
				onUpdateSuccess?.();
			},
			// Refetch the source query so the Apollo cache is updated and every
			// consumer of the available content states (not just this menu's local
			// optimistic copy) reflects the deletion.
			refetchQueries: () => [
				{
					query: EditorContentStateQuery,
					variables: {
						contentId,
						isDraft: !isLivePage,
						shouldSkipContentStateLastUpdated: expValEquals(
							'confluence_approvals_eap',
							'isEnabled',
							true,
						)
							? false
							: !isLivePage,
						isApprovalsFgOn: expValEquals('confluence_approvals_eap', 'isEnabled', true),
						format: expValEquals('confluence_approvals_eap', 'isEnabled', true)
							? 'MILLIS'
							: 'USER_FRIENDLY',
					},
				},
			],
		},
	);

	const [setContentStateAndPublishMutation] = useMutationInterop<
		EditorContentStateComponentSetContentStateAndPublishMutation$data,
		EditorContentStateComponentSetContentStateAndPublishMutation$variables
	>(
		graphql`
			mutation EditorContentStateComponentSetContentStateAndPublishMutation(
				$contentId: ID!
				$contentStateInput: ContentStateInput!
				$suppressDraftPropertyUpdate: Boolean
				$cloudId: ID!
			) {
				setContentStateAndPublish(
					cloudId: $cloudId
					contentId: $contentId
					contentState: $contentStateInput
					suppressDraftPropertyUpdate: $suppressDraftPropertyUpdate
				) {
					name
					color
					id
					restrictionLevel
				}
			}
		`,
		{
			onError: () => {
				isCreatingNewCustomStatusRef.current = false;
				void contentStateState.setContentState(originalContentState.current);
				void flags.showErrorFlag({
					id: 'content-state-error-flag',
					title: <FormattedMessage {...i18n.errorFlagTitle} />,
					description: <FormattedMessage {...i18n.errorFlagBody} />,
				});
				// TODO: Add robust error handling
				createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: {
						action: 'failed',
						actionSubject: 'contentState',
						actionSubjectId: 'setContentStateAndPublish',
						source: 'editorContentState',
						attributes: {
							contentId,
							contentStatus: 'CURRENT',
							isLivePage,
						},
					},
				}).fire();
			},
			onCompleted: (data) => {
				if (data.setContentStateAndPublish && isCreatingNewCustomStatusRef.current) {
					// The approvals-beta flow adds an optimistic placeholder that must be reconciled
					// away; the legacy (gate-off) flow keeps its original dedupe behaviour untouched.
					const newCustomStatesArray = fg('confluence_approvals_beta')
						? reconcileCustomStatesAfterCreate(
								contentStateState.state.customStatesArray,
								data.setContentStateAndPublish,
							)
						: createNewCustomStatesArrayWhenNewCustomAdded(
								contentStateState.state.customStatesArray,
								data.setContentStateAndPublish,
							);
					void contentStateState.setCustomStatesArray(newCustomStatesArray);
					isCreatingNewCustomStatusRef.current = false;
				}
				if (data.setContentStateAndPublish) {
					originalContentState.current = data.setContentStateAndPublish;
				}
				onUpdateSuccess?.();
			},
			refetchQueries: () => [
				{
					query: EditorContentStateQuery,
					variables: {
						contentId,
						isDraft: false,
						shouldSkipContentStateLastUpdated: false,
						isApprovalsFgOn: true,
						format: 'MILLIS',
					},
				},
			],
		},
	);

	const [removeContentStateAndPublishMutation] = useMutationInterop<
		EditorContentStateComponentRemoveContentStateAndPublishMutation$data,
		EditorContentStateComponentRemoveContentStateAndPublishMutation$variables
	>(
		graphql`
			mutation EditorContentStateComponentRemoveContentStateAndPublishMutation(
				$contentId: ID!
				$doPublish: Boolean
				$suppressDraftPropertyUpdate: Boolean
			) {
				removeContentState(
					contentId: $contentId
					doPublish: $doPublish
					suppressDraftPropertyUpdate: $suppressDraftPropertyUpdate
				)
			}
		`,
		{
			onError: () => {
				void contentStateState.setContentState(originalContentState.current);
				void flags.showErrorFlag({
					id: 'content-state-remove-error-flag',
					title: <FormattedMessage {...i18n.removeErrorFlagTitle} />,
					description: <FormattedMessage {...i18n.removeErrorFlagBody} />,
				});
				// TODO: Add robust error handling
				createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: {
						action: 'failed',
						actionSubject: 'contentState',
						actionSubjectId: 'removeContentStateAndPublish',
						source: 'editorContentState',
						attributes: {
							contentId,
							contentStatus: 'CURRENT',
							isLivePage,
						},
					},
				}).fire();
			},
			refetchQueries: () => [
				{
					query: EditorContentStateQuery,
					variables: {
						contentId,
						isDraft: false,
						shouldSkipContentStateLastUpdated: false,
						isApprovalsFgOn: true,
						format: 'MILLIS',
					},
				},
			],
			onCompleted: () => {
				onUpdateSuccess?.();
			},
		},
	);

	const activeRemoveMutation =
		isViewMode && expValEquals('confluence_approvals_eap', 'isEnabled', true)
			? removeContentStateAndPublishMutation
			: removeContentStateMutation;

	const rightSidebarContext = useRightSidebarContext();
	const isApprovalsPaneOpen = rightSidebarContext.getCurrent()?.panelId === APPROVALS_PANE_ID;
	const wasApprovalsPaneOpenRef = useRef(isApprovalsPaneOpen);

	// Close the content state info popup only at the moment the approvals pane opens,
	// not continuously while it is open — users should still be able to reopen the popup.
	// TODO: revisit this logic to see if it can be simplified.
	useEffect(() => {
		if (isApprovalsPaneOpen && !wasApprovalsPaneOpenRef.current && isShowingInfoPopup) {
			setIsShowingInfoPopup(false);
		}
		wasApprovalsPaneOpenRef.current = isApprovalsPaneOpen;
	}, [isApprovalsPaneOpen, isShowingInfoPopup]);

	// Reset color picker when menu closes
	useEffect(() => {
		if (!isContentStateMenuOpen) {
			setSelectedColor(null);
			setIsColorPickerOpen(false);
		}
	}, [isContentStateMenuOpen]);

	const applyExistingStatus = useCallback(
		(selectedStatus: ContentState) => {
			if (isViewMode && !cloudId) {
				return;
			}
			isCreatingNewCustomStatusRef.current = false;
			void contentStateState.setIsUpdating(true);
			if (isViewMode && expValEquals('confluence_approvals_eap', 'isEnabled', true)) {
				void setContentStateAndPublishMutation({
					variables: {
						contentId,
						contentStateInput: {
							color: tokenToHex(selectedStatus.color),
							name: selectedStatus.name,
							id: selectedStatus.id as any,
						},
						suppressDraftPropertyUpdate: true,
						cloudId,
					},
				});
			} else {
				void setContentStateMutation({
					variables: {
						contentId,
						contentStateInput: {
							color: tokenToHex(selectedStatus.color),
							name: selectedStatus.name,
							id: selectedStatus.id as any,
						},
					},
				});
			}
			void contentStateState.setContentState(selectedStatus);

			const customStatesArray = contentStateState.state.customStatesArray;
			const customIndex = customStatesArray.findIndex(
				(s) => s !== null && s.id === selectedStatus.id,
			);
			if (customIndex !== -1) {
				const newCustomStatesArray = createNewCustomStatesArrayWhenExistingCustomSelected(
					customStatesArray,
					selectedStatus,
					customIndex,
				);
				void contentStateState.setCustomStatesArray(newCustomStatesArray);
			}

			if (!isViewMode && shouldSetAutomationFlag && fg('cc_a4c_smart_button_changeboarding')) {
				localStorage.setItem(keys.AUTOMATION_DISCOVER_SET_CONTENT_STATE, true);
				setShouldSetAutomationFlag(false);
			}

			const isCustom = customIndex !== -1;
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					objectId: contentId,
					actionSubjectId: 'editorContentStateAdded',
					source: 'editorContentState',
					attributes: {
						type: isCustom ? 'custom' : 'suggested',
						color: colorCodeToColorName(selectedStatus.color?.toUpperCase() || ''),
						restrictionLevel: selectedStatus.restrictionLevel?.toUpperCase(),
						uiLocation: 'pageTitleSection',
						contentStatus: isViewMode ? 'CURRENT' : 'DRAFT',
						isLivePage,
					},
				},
			}).fire();
		},
		[
			contentId,
			contentStateState,
			createAnalyticsEvent,
			isLivePage,
			isViewMode,
			shouldSetAutomationFlag,
			setContentStateAndPublishMutation,
			setContentStateMutation,
			cloudId,
		],
	);

	const applyCustomStatus = useCallback(
		(statusName: string, colorId: string) => {
			if (isViewMode && !cloudId) {
				return;
			}
			if (
				!isDuplicate(contentStateState.state.contentState, {
					color: colorId.toLowerCase(),
					name: statusName,
				})
			) {
				const newContentState = { color: colorId, name: statusName };
				void contentStateState.setIsUpdating(true);
				isCreatingNewCustomStatusRef.current = true;

				const optimisticCustomState = {
					...newContentState,
					id: `temp-${Date.now()}`,
				};
				const updatedCustomStatesArray = [
					optimisticCustomState,
					...contentStateState.state.customStatesArray,
				];
				void contentStateState.setCustomStatesArray(updatedCustomStatesArray);

				if (isViewMode && expValEquals('confluence_approvals_eap', 'isEnabled', true)) {
					void setContentStateAndPublishMutation({
						variables: {
							contentId,
							contentStateInput: {
								...newContentState,
								color: tokenToHex(colorId),
							},
							suppressDraftPropertyUpdate: true,
							cloudId,
						},
					});
				} else {
					void setContentStateMutation({
						variables: {
							contentId,
							contentStateInput: {
								...newContentState,
								color: tokenToHex(colorId),
							},
						},
					});
				}
				void contentStateState.setContentState(newContentState);
			}

			if (!isViewMode && shouldSetAutomationFlag && fg('cc_a4c_smart_button_changeboarding')) {
				localStorage.setItem(keys.AUTOMATION_DISCOVER_SET_CONTENT_STATE, true);
				setShouldSetAutomationFlag(false);
			}

			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					objectId: contentId,
					actionSubjectId: 'editorContentStateAdded',
					source: 'editorContentState',
					attributes: {
						type: 'new custom',
						color: colorCodeToColorName(colorId),
						restrictionLevel: 'NONE',
						uiLocation: 'pageTitleSection',
						contentStatus: isViewMode ? 'CURRENT' : 'DRAFT',
						isLivePage,
					},
				},
			}).fire();
		},
		[
			contentId,
			contentStateState,
			createAnalyticsEvent,
			isLivePage,
			isViewMode,
			shouldSetAutomationFlag,
			setContentStateAndPublishMutation,
			setContentStateMutation,
			cloudId,
		],
	);

	const handleSelectExistingStatusV2 = useCallback(
		(selectedStatus: ContentState) => {
			if (
				activeWorkflowStatus &&
				approvalRequest?.review &&
				approvalRequest.review.reviewStatus === 'PENDING'
			) {
				setShowChangeStatusModal(true);
				return;
			}

			// End-state approval (APPROVED / DENIED): defer until the user confirms the
			// "Change status?" friction modal, then apply this selection in handleConfirmChangeStatus.
			if (hasEndStateApproval) {
				setPendingStatusChange({ kind: 'existing', status: selectedStatus });
				setShowChangeStatusConfirmModal(true);
				return;
			}

			if (activeWorkflowStatus) {
				clearApprovalRequestOptimistically();
				void cancelWorkflowApplication();
			}

			applyExistingStatus(selectedStatus);
		},
		[
			activeWorkflowStatus,
			approvalRequest,
			hasEndStateApproval,
			cancelWorkflowApplication,
			clearApprovalRequestOptimistically,
			applyExistingStatus,
		],
	);

	const handleSaveCustomStatusV2 = useCallback(
		(statusName: string, colorId: string) => {
			if (
				activeWorkflowStatus &&
				approvalRequest?.review &&
				approvalRequest.review.reviewStatus === 'PENDING'
			) {
				setShowChangeStatusModal(true);
				return;
			}

			// End-state approval (APPROVED / DENIED): defer until the user confirms the
			// "Change status?" friction modal, then apply this selection in handleConfirmChangeStatus.
			if (hasEndStateApproval) {
				setPendingStatusChange({ kind: 'custom', name: statusName, colorId });
				setShowChangeStatusConfirmModal(true);
				return;
			}

			if (activeWorkflowStatus) {
				clearApprovalRequestOptimistically();
				void cancelWorkflowApplication();
			}

			applyCustomStatus(statusName, colorId);
		},
		[
			activeWorkflowStatus,
			approvalRequest,
			hasEndStateApproval,
			cancelWorkflowApplication,
			clearApprovalRequestOptimistically,
			applyCustomStatus,
		],
	);

	const handleDeleteCustomStatusV2 = useCallback(
		(status: ContentState) => {
			if (!fg('confluence_approvals_beta')) {
				return;
			}

			if (!status.id || !cloudId) {
				return;
			}

			// Only one delete may be in flight at a time; the snapshot refs hold a single
			// operation, so concurrent deletes would corrupt rollback on failure.
			if (isDeletingCustomStatusRef.current) {
				return;
			}
			isDeletingCustomStatusRef.current = true;

			const currentCustomStatesArray = contentStateState.state.customStatesArray;
			// Snapshot the option list for rollback on failure.
			customStatesBeforeDeleteRef.current = currentCustomStatesArray;

			// Mark in-flight before the mutation so the refetch can't clobber the optimistic update.
			void contentStateState.setIsUpdating(true);

			// Remove the deleted option from the menu only. The page keeps its applied status
			// (even if it is the one being deleted) and the menu stays open for reselection.
			const updatedCustomStatesArray = currentCustomStatesArray.filter(
				(customStatus) => customStatus?.id !== status.id,
			);
			void contentStateState.setCustomStatesArray(updatedCustomStatesArray);

			void disableCustomContentStateMutation({
				variables: {
					cloudId,
					contentStateId: status.id,
				},
			});

			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					objectId: contentId,
					actionSubjectId: 'editorContentStateDeleted',
					source: 'editorContentState',
					attributes: {
						type: 'custom',
						color: colorCodeToColorName(status.color ?? ''),
						uiLocation: 'pageTitleSection',
						isLivePage,
					},
				},
			}).fire();
		},
		[
			cloudId,
			contentId,
			contentStateState,
			createAnalyticsEvent,
			disableCustomContentStateMutation,
			isLivePage,
		],
	);

	const applyRemove = useCallback(() => {
		isCreatingNewCustomStatusRef.current = false;
		if (contentStateState.state.contentState) {
			void contentStateState.setIsUpdating(true);
			void activeRemoveMutation({
				variables: {
					contentId,
					...(isViewMode &&
						expValEquals('confluence_approvals_eap', 'isEnabled', true) && {
							doPublish: true,
							suppressDraftPropertyUpdate: true,
						}),
				},
			});
			void contentStateState.setContentState(null);

			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					objectId: contentId,
					actionSubjectId: 'editorContentStateRemoved',
					source: 'editorContentState',
					attributes: {
						uiLocation: 'pageTitleSection',
						contentStatus: isViewMode ? 'CURRENT' : 'DRAFT',
						isLivePage,
					},
				},
			}).fire();
		}
	}, [
		contentStateState,
		activeRemoveMutation,
		contentId,
		createAnalyticsEvent,
		isLivePage,
		isViewMode,
	]);

	const handleRemoveV2 = useCallback(() => {
		if (
			activeWorkflowStatus &&
			approvalRequest?.review &&
			approvalRequest.review.reviewStatus === 'PENDING'
		) {
			setShowChangeStatusModal(true);
			return;
		}

		// End-state approval (APPROVED / DENIED): defer until the user confirms the
		// "Change status?" friction modal, then apply the removal in handleConfirmChangeStatus.
		if (hasEndStateApproval) {
			setPendingStatusChange({ kind: 'remove' });
			setShowChangeStatusConfirmModal(true);
			return;
		}

		if (activeWorkflowStatus) {
			clearApprovalRequestOptimistically();
			void cancelWorkflowApplication();
		}

		setIsContentStateMenuOpenWithListener(false);
		applyRemove();
	}, [
		activeWorkflowStatus,
		approvalRequest,
		hasEndStateApproval,
		clearApprovalRequestOptimistically,
		cancelWorkflowApplication,
		setIsContentStateMenuOpenWithListener,
		applyRemove,
	]);

	// Performs the actual "start approval" work for a review-eligible status. This is the
	// guard-free core of handleSelectWorkflowStatus so it can be reused after the user confirms
	// the "Change status?" friction modal (where the end-state approval guard must not re-trigger).
	const startWorkflowStatus = useCallback(
		async (selectedStatus: WorkflowStatus) => {
			const previousContentState = contentStateState.state.contentState;
			const prevStateId = activeWorkflowStatus?.id || contentState?.id || null;
			const analyticsAttributes = {
				contentId,
				contentStatus: isViewMode ? 'CURRENT' : 'DRAFT',
				prevStateId,
				newStateId: selectedStatus.id,
				isNewStatusWorkflow: true,
			};

			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'inReviewStatus',
					source: APPROVALS_ANALYTICS_SOURCE,
					attributes: analyticsAttributes,
				},
			}).fire();

			/**
			 * Live-doc → convert-to-page popup interception.
			 *
			 * Approvals can't run on live docs, so when a review-eligible status is selected
			 * here we close the status menu and show the convert-to-page intermediary popup.
			 * The popup's CTA then dispatches the intent onto ApprovalsContext, which causes
			 * PublishModalMountPoint to auto-open the publish modal in convert-to-page mode.
			 *
			 */
			if (selectedStatus.allowsReviews && isLivePage && fg('confluence_approvals_beta')) {
				setIsContentStateMenuOpenWithListener(false);
				setIsShowingLiveDocStartApprovalPopup(true);
				return;
			}

			if (fg('confluence_approvals_beta')) {
				// New flow: no optimistic In-Review status, no createWorkflowApplication call.
				// The combined mutation is called later from StartApprovalPane.
				setIsContentStateMenuOpenWithListener(false);
				if (otherModeReviewStatus === 'PENDING') {
					setIsShowingOtherModeBlockingPopup(true);
				} else {
					openApprovalsPaneForContext({
						contentId,
						contentStatus: isViewMode ? 'CURRENT' : 'DRAFT',
						rightSidebarContext,
					});
				}
				return;
			}

			void contentStateState.setIsUpdating(true);
			void contentStateState.setContentState(null);
			setIsContentStateMenuOpenWithListener(false);

			try {
				const result = await createWorkflowApplication(selectedStatus);

				if (otherModeReviewStatus === 'PENDING') {
					// Other mode already has an active pending review — show the info popup
					// (which renders the otherModeReviewWarning SectionMessage) instead of
					// opening the ApprovalsPane / StartApprovalPane.
					setIsShowingInfoPopup(true);
				} else {
					openApprovalsPaneForContext({
						contentId,
						contentStatus: isViewMode ? 'CURRENT' : 'DRAFT',
						rightSidebarContext,
					});
				}
				createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: {
						action: 'created',
						actionSubject: 'approvalWorkflowApplication',
						source: APPROVALS_ANALYTICS_SOURCE,
						attributes: { ...analyticsAttributes, applicationId: result.applicationId },
					},
				}).fire();
				void contentStateState.setIsUpdating(false);
			} catch (error) {
				void contentStateState.setIsUpdating(false);
				void contentStateState.setContentState(previousContentState);
				void flags.showErrorFlag({
					id: 'workflow-status-error-flag',
					title: <FormattedMessage {...i18n.errorFlagTitle} />,
					description: <FormattedMessage {...i18n.errorFlagBody} />,
				});
				createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: {
						action: 'failed',
						actionSubject: 'approvalWorkflowApplication',
						source: APPROVALS_ANALYTICS_SOURCE,
						attributes: {
							...analyticsAttributes,
							failureStatusCode: getFailureStatusCode(error),
						},
					},
				}).fire();
			}
		},
		[
			otherModeReviewStatus,
			createWorkflowApplication,
			contentStateState,
			contentState,
			contentId,
			isLivePage,
			isViewMode,
			activeWorkflowStatus,
			createAnalyticsEvent,
			flags,
			setIsContentStateMenuOpenWithListener,
			setIsShowingLiveDocStartApprovalPopup,
			setIsShowingOtherModeBlockingPopup,
			rightSidebarContext,
		],
	);

	const handleSelectWorkflowStatus = useCallback(
		(selectedStatus: WorkflowStatus) => {
			// End-state approval (APPROVED / DENIED): selecting "In Review" again must first
			// confirm via the "Change status?" friction modal. On confirm we cancel the old
			// approval and start a fresh approval request (handleConfirmChangeStatus).
			if (hasEndStateApproval) {
				setPendingStatusChange({ kind: 'workflow', status: selectedStatus });
				setShowChangeStatusConfirmModal(true);
				return;
			}

			void startWorkflowStatus(selectedStatus);
		},
		[hasEndStateApproval, startWorkflowStatus],
	);

	const handleColorPickerToggle = useCallback(() => {
		setIsColorPickerOpen((prev) => !prev);
	}, []);

	const handleColorPickerClose = useCallback(() => {
		setIsColorPickerOpen(false);
	}, []);

	const handleSelectColor = useCallback((newColor: string) => {
		setSelectedColor(newColor);
		setIsColorPickerOpen(false);
	}, []);

	const handleFocusTextField = useCallback(() => {
		setIsCreatingContentState(true);
	}, []);

	const handleMouseEnterRemove = useCallback(() => {
		setIsHoveringRemove(true);
	}, []);

	const handleMouseLeaveRemove = useCallback(() => {
		setIsHoveringRemove(false);
	}, []);

	const handleContentStatePopupClose = useCallback(() => {
		setIsShowingInfoPopup(false);
		setIsCreatingContentState(false);
		setIsContentStateMenuOpenWithListener(false);
	}, [setIsContentStateMenuOpenWithListener]);

	const handleSubmit = useCallback(
		(name: string) => {
			if (
				!isDuplicate(contentStateState.state.contentState, {
					color: selectedColor?.toLowerCase() ?? null,
					name,
				})
			) {
				const newContentState = { color: selectedColor, name };
				void contentStateState.setIsUpdating(true);
				// Mark that we're creating a new custom status so onCompleted updates the array
				isCreatingNewCustomStatusRef.current = true;
				void setContentStateMutation({
					variables: {
						contentId,
						contentStateInput: {
							...newContentState,
							color: tokenToHex(selectedColor),
						},
					},
				});
				void contentStateState.setContentState(newContentState);
			}
			if (shouldSetAutomationFlag && fg('cc_a4c_smart_button_changeboarding')) {
				// This flag is used in AutomationMenuButtonWithSmartButtonSpotlight
				// It will check this flag to determine if it should show the spotlight
				localStorage.setItem(keys.AUTOMATION_DISCOVER_SET_CONTENT_STATE, true);
				setShouldSetAutomationFlag(false);
			}
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					objectId: contentId,
					actionSubjectId: 'editorContentStateAdded',
					source: 'editorContentState',
					attributes: {
						type: 'new custom',
						color: colorCodeToColorName(selectedColor ?? ''),
						restrictionLevel: 'NONE',
						uiLocation: 'pageTitleSection',
						contentStatus: isViewMode ? 'CURRENT' : 'DRAFT',
						isLivePage,
					},
				},
			}).fire();
			setIsCreatingContentState(false);
			setIsContentStateMenuOpenWithListener(false);
		},
		[
			contentId,
			contentStateState,
			createAnalyticsEvent,
			isLivePage,
			isViewMode,
			selectedColor,
			setContentStateMutation,
			setIsContentStateMenuOpenWithListener,
			shouldSetAutomationFlag,
		],
	);

	const handleCancel = useCallback(() => {
		setIsCreatingContentState(false);
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				objectId: contentId,
				actionSubjectId: 'editorContentStateCancel',
				source: 'editorContentState',
				attributes: {
					contentStatus: isViewMode ? 'CURRENT' : 'DRAFT',
					isLivePage,
				},
			},
		}).fire();
	}, [contentId, createAnalyticsEvent, isLivePage, isViewMode]);

	const handleRemove = useCallback(() => {
		// check if state exists before mutating since the trash button still shows if the feature is disabled
		// (page might not have a content state)
		if (contentStateState.state.contentState) {
			void removeContentStateMutation({
				variables: {
					contentId,
				},
			});
			setIsCreatingContentState(false);
			setIsHoveringRemove(false);
			void contentStateState.setContentState(null);
			setIsContentStateMenuOpenWithListener(false);
		}
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				objectId: contentId,
				actionSubjectId: 'editorContentStateRemoved',
				source: 'editorContentState',
				attributes: {
					uiLocation: 'pageTitleSection',
					contentStatus: isViewMode ? 'CURRENT' : 'DRAFT',
					isLivePage,
				},
			},
		}).fire();
	}, [
		contentId,
		contentStateState,
		createAnalyticsEvent,
		removeContentStateMutation,
		setIsCreatingContentState,
		setIsContentStateMenuOpenWithListener,
		isLivePage,
		isViewMode,
	]);

	const handlePopupToggle = useCallback(() => {
		setIsContentStateMenuOpenWithListener(!isContentStateMenuOpen);
	}, [isContentStateMenuOpen, setIsContentStateMenuOpenWithListener]);

	const handlePopupClose = useCallback(() => {
		setIsShowingInfoPopup(false);
		setIsShowingOtherModeBlockingPopup(false);
		setIsShowingLiveDocStartApprovalPopup(false);
		setIsContentStateMenuOpenWithListener(false);
	}, [setIsContentStateMenuOpenWithListener]);

	/**
	 * Live-doc popup "Convert to page" CTA handler.
	 *
	 * Dispatches the intent onto ApprovalsContext (the only shared dependency between
	 * content-state and publish-modal), which causes PublishModalMountPoint to auto-open
	 * the existing publish modal in convert-to-page mode. No imperative cross-package call
	 * from content-state means no circular dependency.
	 *
	 * Gate-first: the dispatch action itself is a no-op when `confluence_approvals_beta`
	 * is off, so the feature is fully dormant in that state.
	 */
	const handleConvertLiveDocToPage = useCallback(() => {
		requestLiveDocApprovalConversion(contentId);
		handlePopupClose();
	}, [contentId, handlePopupClose, requestLiveDocApprovalConversion]);

	const handleBackFromLiveDocStartApprovalPopup = useCallback(() => {
		setIsShowingLiveDocStartApprovalPopup(false);
		setIsContentStateMenuOpenWithListener(true);
	}, [setIsContentStateMenuOpenWithListener]);

	const [showChangeStatusConfirmModal, setShowChangeStatusConfirmModal] = useState(false);

	// A status the user picked while an end-state approval is active, pending their
	// confirmation in the "Change status?" friction modal. Applied on confirm, cleared on dismiss.
	const [pendingStatusChange, setPendingStatusChange] = useState<PendingStatusChange | null>(null);

	const handleChangeStatus = useCallback(() => {
		setIsShowingInfoPopup(false);
		// Defer the friction confirmation: just open the status menu. The "Change status?"
		// confirmation modal is now shown only once the user actually selects a different
		// (non-approval) status — see handleSelectExistingStatusV2 / handleSaveCustomStatusV2 /
		// handleRemoveV2.
		setIsContentStateMenuOpenWithListener(true);
	}, [setIsContentStateMenuOpenWithListener]);

	const handleConfirmChangeStatus = useCallback(() => {
		setShowChangeStatusConfirmModal(false);

		const pending = pendingStatusChange;
		setPendingStatusChange(null);

		if (!pending) {
			// Defensive: no deferred selection — just reopen the menu without cancelling the
			// existing approval (cancelling here would silently discard an active approval for
			// no reason if `pending` is ever null due to a stale closure or batched update).
			setIsContentStateMenuOpenWithListener(true);
			return;
		}

		// Cancel the existing approval so the selected status can be applied / a new approval
		// request can be started. The workflow application is deactivated; prior activity
		// remains available in audit logs (matching the modal copy).
		clearApprovalRequestOptimistically();
		void cancelWorkflowApplication();

		setIsContentStateMenuOpenWithListener(false);
		switch (pending.kind) {
			case 'existing':
				applyExistingStatus(pending.status);
				break;
			case 'custom':
				applyCustomStatus(pending.name, pending.colorId);
				break;
			case 'remove':
				applyRemove();
				break;
			case 'workflow':
				// Start a fresh approval request now that the previous approval has been cancelled.
				void startWorkflowStatus(pending.status);
				break;
			default:
				pending satisfies never;
		}
	}, [
		clearApprovalRequestOptimistically,
		cancelWorkflowApplication,
		pendingStatusChange,
		applyExistingStatus,
		applyCustomStatus,
		applyRemove,
		startWorkflowStatus,
		setIsContentStateMenuOpenWithListener,
	]);

	const handleCloseChangeStatusConfirmModal = useCallback(() => {
		setShowChangeStatusConfirmModal(false);
		setPendingStatusChange(null);
		setIsShowingInfoPopup(true);
	}, []);

	const [showCancelApprovalModal, setShowCancelApprovalModal] = useState(false);

	const handleClearRequest = useCallback(() => {
		setIsShowingInfoPopup(false);
		setShowCancelApprovalModal(true);
	}, []);

	const handleConfirmClearRequest = useCallback(() => {
		setShowCancelApprovalModal(false);
		setIsContentStateMenuOpenWithListener(false);

		clearApprovalRequestOptimistically();
		void cancelWorkflowApplication();
	}, [
		clearApprovalRequestOptimistically,
		cancelWorkflowApplication,
		setIsContentStateMenuOpenWithListener,
	]);

	const handleCloseCancelApprovalModal = useCallback(() => {
		setShowCancelApprovalModal(false);
		setIsShowingInfoPopup(true);
	}, []);

	const handleConfirmCancelApproval = useCallback(() => {
		setShowChangeStatusModal(false);
		setIsContentStateMenuOpenWithListener(false);

		clearApprovalRequestOptimistically();
		void cancelWorkflowApplication();
	}, [
		clearApprovalRequestOptimistically,
		cancelWorkflowApplication,
		setIsContentStateMenuOpenWithListener,
	]);

	const handleCloseChangeStatusModal = useCallback(() => {
		setShowChangeStatusModal(false);
		setIsContentStateMenuOpenWithListener(true);
	}, [setIsContentStateMenuOpenWithListener]);

	const togglePopup = useCallback(() => {
		const shouldShowInfoPopup =
			expValEquals('confluence_approvals_eap', 'isEnabled', true) &&
			((isContentApprovalsEnabled && activeWorkflowStatus !== null) ||
				// Show the read-only info popup for view-only users who have a content state
				(!canEdit && contentState !== null));
		if (shouldShowInfoPopup) {
			if (isShowingInfoPopup) {
				setIsShowingInfoPopup(false);
			} else {
				setIsShowingInfoPopup(true);
			}
		} else if (canEdit) {
			// Only allow opening the content state menu if the user has edit permission
			handlePopupToggle();
		}
		onClick?.();
	}, [
		isContentApprovalsEnabled,
		activeWorkflowStatus,
		isShowingInfoPopup,
		handlePopupToggle,
		onClick,
		canEdit,
		contentState,
	]);

	const renderTrigger = useCallback(
		(triggerProps: any) => (
			<ContentStateButton
				triggerProps={{ ...triggerProps }}
				togglePopup={togglePopup}
				contentState={contentStateState.state.contentState}
				activeWorkflowStatus={activeWorkflowStatus}
				isSelected={isContentStateMenuOpen}
				isWarning={isHoveringRemove}
				userOffline={userOffline}
				contentStateButtonRef={contentStateButtonRef}
				floatingToolbarButtonStyle={floatingToolbarButtonStyle}
				renderPlusButtonEmptyState={renderPlusButtonEmptyState}
				isDetailsPanel={isDetailsPanel}
				canEdit={canEdit}
			/>
		),
		[
			togglePopup,
			contentStateState.state.contentState,
			activeWorkflowStatus,
			isContentStateMenuOpen,
			isHoveringRemove,
			userOffline,
			floatingToolbarButtonStyle,
			renderPlusButtonEmptyState,
			isDetailsPanel,
			canEdit,
		],
	);

	if (
		!renderServerPlaceholder &&
		isContentApprovalsEnabled &&
		approvalRequestLoading &&
		expValEquals('confluence_approvals_eap', 'isEnabled', true)
	) {
		return <ContentStateSkeleton />;
	}

	return (
		<>
			<Popup
				testId="content-state-popup"
				isOpen={
					isContentStateMenuOpen ||
					isShowingInfoPopup ||
					isShowingOtherModeBlockingPopup ||
					isShowingLiveDocStartApprovalPopup
				}
				onClose={handlePopupClose}
				placement={forceContentStatePlacementToTop ? 'top-end' : 'bottom-start'}
				zIndex={layers.dialog()}
				trigger={renderTrigger}
				content={({ update }) => {
					if (isShowingOtherModeBlockingPopup) {
						return (
							<OtherModeReviewBlockingPopup
								isViewMode={isViewMode ?? false}
								onChangeStatus={() => {
									setIsShowingOtherModeBlockingPopup(false);
									handleChangeStatus();
								}}
							/>
						);
					}

					if (isShowingLiveDocStartApprovalPopup) {
						return (
							<LiveDocStartApprovalPopup
								onBack={handleBackFromLiveDocStartApprovalPopup}
								onConvertToPage={handleConvertLiveDocToPage}
							/>
						);
					}

					if (isShowingInfoPopup) {
						return (
							<ContentStateInfoPopup
								contentState={contentState}
								activeWorkflowStatus={activeWorkflowStatus}
								updatedAt={updatedAt}
								updatedBy={updatedBy}
								loading={userDataLoading}
								onChangeStatus={handleChangeStatus}
								onClearRequest={handleClearRequest}
								onClose={handlePopupClose}
								onConvertLiveDocToPage={handleConvertLiveDocToPage}
								canEdit={canEdit}
								contentType={contentType}
								isViewMode={isViewMode}
							/>
						);
					}

					if (expValEquals('confluence_approvals_eap', 'isEnabled', true)) {
						return (
							<ContentStateMenuV2
								contentId={contentId}
								contentStateState={contentStateState}
								contentType={contentType}
								isLivePage={isLivePage}
								defaultStatesArray={contentStateState.state.defaultStatesArray || []}
								customStatesArray={contentStateState.state.customStatesArray || []}
								isContentStateEnabled={contentStateState.state.isContentStateEnabled}
								userOffline={userOffline}
								onSelectExistingStatus={handleSelectExistingStatusV2}
								onSaveCustomStatus={handleSaveCustomStatusV2}
								onDeleteCustomStatus={handleDeleteCustomStatusV2}
								onRemove={handleRemoveV2}
								isContentStateMenuOpen={isContentStateMenuOpen}
								setIsContentStateMenuOpen={setIsContentStateMenuOpenWithListener}
								workflowStatuses={reviewEligibleStatuses}
								onSelectWorkflowStatus={handleSelectWorkflowStatus}
							/>
						);
					}

					return (
						<ContentStateMenu
							contentId={contentId}
							contentStateState={contentStateState}
							contentType={contentType}
							isContentStateEnabled={contentStateState.state.isContentStateEnabled}
							isSuggestedEnabled={contentStateState.state.isSuggestedEnabled}
							isCustomEnabled={contentStateState.state.isCustomEnabled}
							customStatesArray={contentStateState.state.customStatesArray}
							defaultStatesArray={contentStateState.state.defaultStatesArray}
							isCreatingContentState={isCreatingContentState}
							selectedColor={selectedColor}
							onSelectColor={handleSelectColor}
							isColorPickerOpen={isColorPickerOpen}
							onColorPickerToggle={handleColorPickerToggle}
							onColorPickerClose={handleColorPickerClose}
							onPopupClose={handleContentStatePopupClose}
							onSubmit={handleSubmit}
							onCancel={handleCancel}
							onRemove={handleRemove}
							onFocusTextField={handleFocusTextField}
							onMouseEnterRemove={handleMouseEnterRemove}
							onMouseLeaveRemove={handleMouseLeaveRemove}
							userOffline={userOffline}
							contentStateButtonRef={contentStateButtonRef}
							onUpdateSuccess={onUpdateSuccess}
							isContentStateMenuOpen={isContentStateMenuOpen}
							setIsContentStateMenuOpen={setIsContentStateMenuOpenWithListener}
							updatePopup={update}
						/>
					);
				}}
			/>
			{showChangeStatusModal && (
				<ChangeStatusAndCancelApprovalModal
					approverData={respondedApproverData}
					onCancelApproval={handleConfirmCancelApproval}
					onClose={handleCloseChangeStatusModal}
				/>
			)}
			{showCancelApprovalModal && (
				<CancelApprovalModal
					approverData={respondedApproverData}
					onCancelApproval={handleConfirmClearRequest}
					onClose={handleCloseCancelApprovalModal}
				/>
			)}
			{showChangeStatusConfirmModal && (
				<ApprovalActionConfirmationModal
					variant="changeStatus"
					onConfirm={handleConfirmChangeStatus}
					onClose={handleCloseChangeStatusConfirmModal}
				/>
			)}
		</>
	);
};

export const EditorContentStateComponent: React.ComponentType<
	Omit<EditorContentStateProps, 'flags'>
> = withFlags(EditorContentStateComponentImpl);
